// Layout — v4 (Feb 2026) collapsible left sidebar.
//
// Architectural switch from the horizontal top-nav (v3) to a compact
// left-hand collapsible sidebar sitting under a slim brand header.
// Collapsed state persisted in localStorage. Groups (Raw Material,
// Processes, Reports, Masters) expand inline; single links jump straight.
//
// Palette anchored to the brand: Space-Cadet Blue (#1D2951) primary +
// Orangish-Golden accent (#D4A843).

import { useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import {
  SquaresFour, Package, ClipboardText, SignOut,
  Scissors, Gear, PaintBrush, Truck, PaperPlaneTilt,
  Wrench, Wall, Users, Warehouse, Flame, CaretDown, CaretRight, Cube, Drop,
  List, ChartBar, GearSix, Notebook, Archive,
  Stack, ListChecks, WarningOctagon, UsersThree, Factory, Calculator,
  SidebarSimple, X, Buildings,
} from "@phosphor-icons/react";
import PeriodSelector from "./PeriodSelector";

const LOGO_URL = "/swami-icon.png";
const SIDEBAR_STATE_KEY = "swami.sidebar.collapsed";

const ICON_BY_CODE = {
  cutting: Scissors, incoming_store: Wrench, machining_p1: Gear, machining_p2: Gear,
  semi_firewall: Wrench, painting: PaintBrush, silver_plating: Drop, anodising: Drop,
  nickel_plating: Drop, zinc_plating: Drop, phosphating: Drop, hardening: Flame,
  nitriding: Flame, welding: Flame, outsource: Truck, outsource_cutting: Truck,
  outsource_machining: Truck, firewall: Wall, dispatch: PaperPlaneTilt,
};
const iconFor = (code) => ICON_BY_CODE[code] || Gear;

function useProcessesGlobal() {
  const [processes, setProcesses] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/processes`)
      .then(r => setProcesses((r.data || []).slice().sort((a, b) => (a.sequence || 0) - (b.sequence || 0))))
      .catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("processes:changed", h);
    return () => window.removeEventListener("processes:changed", h);
  }, [load]);
  return processes;
}

function useTopMeta() {
  const [meta, setMeta] = useState({ companyName: "", hasJobWork: false });
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/company/config`).catch(() => ({ data: {} })),
      axios.get(`${API}/parts`).catch(() => ({ data: [] })),
    ]).then(([c, p]) => {
      setMeta({
        companyName: (c.data && c.data.name) || "",
        hasJobWork: (p.data || []).some(x => x.is_job_work || x.job_work_customer),
      });
    });
  }, []);
  return meta;
}

// ─── Sidebar row (link) ────────────────────────────────────────────
function SideLink({ collapsed, active, onClick, icon: Icon, label, testId, depth = 0 }) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={`w-full flex items-center gap-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
        collapsed ? "justify-center px-0 py-2.5" : `pl-${3 + depth * 3} pr-3 py-2`
      } ${
        active
          ? "bg-[#D4A843] text-[#1D2951] shadow-sm"
          : "text-slate-200 hover:bg-white/10 hover:text-white"
      }`}
      style={!collapsed ? { paddingLeft: `${12 + depth * 14}px` } : {}}
      data-testid={testId}
    >
      {Icon && <Icon size={17} weight={active ? "fill" : "regular"} className={active ? "text-[#1D2951]" : "text-[#D4A843]"} />}
      {!collapsed && <span className="flex-1 text-left truncate">{label}</span>}
    </button>
  );
}

// ─── Sidebar group (expandable header) ─────────────────────────────
function SideGroup({ collapsed, active, open, onToggle, icon: Icon, label, testId, children }) {
  // In collapsed mode: render a flyout on hover
  if (collapsed) {
    return (
      <div className="relative group">
        <button
          onClick={onToggle}
          title={label}
          className={`w-full flex items-center justify-center px-0 py-2.5 rounded-lg transition-all ${
            active ? "bg-[#D4A843] text-[#1D2951] shadow-sm" : "text-slate-200 hover:bg-white/10"
          }`}
          data-testid={testId}
        >
          {Icon && <Icon size={17} weight={active ? "fill" : "regular"} className={active ? "text-[#1D2951]" : "text-[#D4A843]"} />}
        </button>
        <div className="hidden group-hover:block absolute left-full top-0 ml-1 z-50 min-w-[220px] bg-[#1D2951] border border-[#D4A843]/30 rounded-lg shadow-2xl py-1.5">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-[#D4A843] border-b border-white/10 mb-1">{label}</div>
          <div className="flex flex-col">{children}</div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-3 pl-3 pr-2 py-2 rounded-lg text-sm font-semibold transition-all ${
          active
            ? "bg-white/10 text-white"
            : "text-slate-200 hover:bg-white/10 hover:text-white"
        }`}
        data-testid={testId}
      >
        {Icon && <Icon size={17} weight={active ? "fill" : "regular"} className={active ? "text-[#D4A843]" : "text-[#D4A843]"} />}
        <span className="flex-1 text-left truncate">{label}</span>
        {open ? <CaretDown size={12} weight="bold" className="opacity-70" /> : <CaretRight size={12} weight="bold" className="opacity-70" />}
      </button>
      {open && <div className="mt-0.5 mb-1 flex flex-col gap-0.5">{children}</div>}
    </div>
  );
}

function SectionLabel({ collapsed, children }) {
  if (collapsed) return <div className="my-2 mx-3 border-t border-white/10" />;
  return (
    <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest font-bold text-[#D4A843]/70">
      {children}
    </div>
  );
}

// ─── Sidebar body (shared between desktop rail + mobile drawer) ─
function SidebarBody({ isCollapsed, forceExpanded = false,
  user, hasJobWork, otherProcs, machining,
  inhouseProcs, outsourceProcs, anyOutsource,
  isActive, rmActive, processActive, reportsActive, costActive, mastersActive,
  openGroups, toggleGroup, go, logout, navigate }) {
  const c = forceExpanded ? false : isCollapsed;

  // ── Special-Access filter ──────────────────────────────────────────
  // Creator & Admin always see everything. Any other role with a
  // configured allowed_sections list sees ONLY those keys. If the list
  // is empty / null, no restriction is applied (back-compat).
  const canSee = useCallback((key) => {
    if (user?.role === "Creator" || user?.role === "Admin") return true;
    const list = user?.allowed_sections;
    if (!Array.isArray(list) || list.length === 0) return true;
    return list.includes(key);
  }, [user]);
  const anyIn = (keys) => keys.some(canSee);
  return (
    <nav className="flex flex-col gap-0.5 py-2 px-2 overflow-y-auto flex-1">
      {canSee("dashboard") && (
        <SideLink collapsed={c} active={isActive("/")} onClick={() => go("/")}
          icon={SquaresFour} label="Dashboard" testId="nav-dashboard" />
      )}

      {canSee("sales-register") && (
        <SideLink collapsed={c} active={isActive("/sales-register")} onClick={() => go("/sales-register")}
          icon={ClipboardText} label="Sales Register" testId="nav-sales" />
      )}

      {canSee("production-planning") && (
        <SideLink collapsed={c} active={isActive("/production-planning")} onClick={() => go("/production-planning")}
          icon={Notebook} label="Production Planning" testId="nav-production-planning" />
      )}

      {anyIn(["raw-material","rm-planning","rm-opening-stock"]) && (
        <SectionLabel collapsed={c}>Operations</SectionLabel>
      )}

      {anyIn(["raw-material","rm-planning","rm-opening-stock"]) && (
        <SideGroup collapsed={c} active={rmActive} open={openGroups.rm}
          onToggle={() => toggleGroup("rm")} icon={Cube} label="Raw Material" testId="nav-rm">
          {canSee("raw-material") && (user?.role === "Admin" || user?.role === "Creator") && (
            <SideLink collapsed={c} depth={1} active={isActive("/raw-material")}
              onClick={() => go("/raw-material")} icon={Cube} label="RM Entry" testId="nav-rm-entry" />
          )}
          {canSee("rm-planning") && (
            <SideLink collapsed={c} depth={1} active={isActive("/rm-planning")}
              onClick={() => go("/rm-planning")} icon={ClipboardText} label="RM Planning" testId="nav-rm-planning" />
          )}
          {canSee("rm-opening-stock") && (
            <SideLink collapsed={c} depth={1} active={isActive("/rm-opening-stock")}
              onClick={() => go("/rm-opening-stock")} icon={Package} label="RM Opening Stock" testId="nav-rm-opening-stock" />
          )}
        </SideGroup>
      )}

      {(() => {
        const visibleInhouse = (anyOutsource ? inhouseProcs : otherProcs).filter(p => canSee(`operations/${p.code}`));
        const visibleMach = machining.filter(m => canSee(`operations/${m.code}`));
        const visibleOutsource = outsourceProcs.filter(p => canSee(`operations/${p.code}`));
        const hasJobWorkVisible = hasJobWork && canSee("job-work");
        const anyChild = visibleInhouse.length + visibleMach.length + visibleOutsource.length + (hasJobWorkVisible ? 1 : 0) > 0;
        if (!anyChild) return null;
        return (
          <SideGroup collapsed={c} active={processActive} open={openGroups.proc}
            onToggle={() => toggleGroup("proc")} icon={Factory} label="Processes" testId="nav-processes">
            {anyOutsource && visibleInhouse.length > 0 && !c && (
              <div className="px-3 pt-2 pb-1 text-[9px] font-black uppercase tracking-widest text-[#166534]">
                In-house
              </div>
            )}
            {visibleInhouse.map(p => (
              <SideLink key={`ih-${p.code}`} collapsed={c} depth={1}
                active={isActive(`/operations/${p.code}`)}
                onClick={() => go(`/operations/${p.code}`)}
                icon={iconFor(p.code)} label={p.name}
                testId={`nav-proc-${p.code}`} />
            ))}
            {visibleMach.map(m => (
              <SideLink key={m.code} collapsed={c} depth={1}
                active={isActive(`/operations/${m.code}`)}
                onClick={() => go(`/operations/${m.code}`)}
                icon={iconFor(m.code)} label={m.name}
                testId={`nav-proc-${m.code}`} />
            ))}
            {hasJobWorkVisible && (
              <SideLink collapsed={c} depth={1} active={isActive("/job-work")}
                onClick={() => go("/job-work")} icon={Package} label="Job Work" testId="nav-job-work" />
            )}
            {visibleOutsource.length > 0 && (
              <>
                {!c && (
                  <div className="px-3 pt-3 pb-1 text-[9px] font-black uppercase tracking-widest text-[#B45309]">
                    Outsource
                  </div>
                )}
                {visibleOutsource.map(p => (
                  <SideLink key={`os-${p.code}`} collapsed={c} depth={1}
                    active={isActive(`/operations/${p.code}?loc=outsource`)}
                    onClick={() => go(`/operations/${p.code}?loc=outsource`)}
                    icon={iconFor(p.code)} label={p.name}
                    testId={`nav-proc-os-${p.code}`} />
                ))}
              </>
            )}
          </SideGroup>
        );
      })()}

      {anyIn(["final-report","work-order","daily-review","inventory","suppliers","stock-at-supplier"]) && (
        <SideGroup collapsed={c} active={reportsActive} open={openGroups.rep}
          onToggle={() => toggleGroup("rep")} icon={ChartBar} label="Reports" testId="nav-reports">
          {canSee("final-report") && (
            <SideLink collapsed={c} depth={1} active={isActive("/final-report")}
              onClick={() => go("/final-report")} icon={ClipboardText} label="Final Report" testId="nav-final-report" />
          )}
          {canSee("work-order") && (
            <SideLink collapsed={c} depth={1} active={isActive("/work-order")}
              onClick={() => go("/work-order")} icon={Notebook} label="Work Order" testId="nav-work-order" />
          )}
          {canSee("daily-review") && (
            <SideLink collapsed={c} depth={1} active={isActive("/daily-review")}
              onClick={() => go("/daily-review")} icon={ChartBar} label="Daily Review Sheet" testId="nav-drs" />
          )}
          {canSee("inventory") && (
            <SideLink collapsed={c} depth={1} active={isActive("/inventory")}
              onClick={() => go("/inventory")} icon={Archive} label="Inventory" testId="nav-inventory" />
          )}
          {canSee("suppliers") && (
            <SideLink collapsed={c} depth={1} active={isActive("/suppliers")}
              onClick={() => go("/suppliers")} icon={Users} label="Suppliers" testId="nav-suppliers" />
          )}
          {canSee("stock-at-supplier") && (
            <SideLink collapsed={c} depth={1} active={isActive("/stock-at-supplier")}
              onClick={() => go("/stock-at-supplier")} icon={Warehouse} label="Stock at Supplier" testId="nav-stock-supplier" />
          )}
        </SideGroup>
      )}

      {anyIn(["cost-sheet","budget-schedule","performance-dispatch"]) && (
        <>
          <SectionLabel collapsed={c}>Costing</SectionLabel>
          <SideGroup collapsed={c} active={costActive} open={openGroups.cost}
            onToggle={() => toggleGroup("cost")} icon={Calculator} label="Costing" testId="nav-costing">
            {canSee("cost-sheet") && (
              <SideLink collapsed={c} depth={1} active={isActive("/cost-sheet")}
                onClick={() => go("/cost-sheet")} icon={Calculator} label="Cost Sheet" testId="nav-cost-sheet" />
            )}
            {canSee("budget-schedule") && (
              <SideLink collapsed={c} depth={1} active={isActive("/budget-schedule")}
                onClick={() => go("/budget-schedule")} icon={Notebook} label="Budget as per Schedule" testId="nav-budget-schedule" />
            )}
            {canSee("performance-dispatch") && (
              <SideLink collapsed={c} depth={1} active={isActive("/performance-dispatch")}
                onClick={() => go("/performance-dispatch")} icon={ChartBar} label="Performance as per Dispatch" testId="nav-performance-dispatch" />
            )}
          </SideGroup>
        </>
      )}

      {anyIn(["customer-complaints","quality-dashboard"]) && (
        <>
          <SectionLabel collapsed={c}>Quality</SectionLabel>
          {canSee("customer-complaints") && (
            <SideLink collapsed={c} active={isActive("/customer-complaints")}
              onClick={() => go("/customer-complaints")} icon={WarningOctagon}
              label="Customer Complaints" testId="nav-customer-complaints" />
          )}
          {canSee("quality-dashboard") && (
            <SideLink collapsed={c} active={isActive("/quality-dashboard")}
              onClick={() => go("/quality-dashboard")} icon={ChartBar}
              label="Quality Dashboard" testId="nav-quality-dashboard" />
          )}
        </>
      )}

      {anyIn(["part-master","bom-master","process-master","customer-master","company-master","machine-master","bom-routing","bought-out","defect-master"]) && (
        <>
          <SectionLabel collapsed={c}>Masters</SectionLabel>
          <SideGroup collapsed={c} active={mastersActive} open={openGroups.mst}
            onToggle={() => toggleGroup("mst")} icon={Stack} label="Masters" testId="nav-masters">
            {canSee("part-master") && (
              <SideLink collapsed={c} depth={1} active={isActive("/part-master")}
                onClick={() => go("/part-master")} icon={Package} label="Part Master" testId="nav-part-master" />
            )}
            {canSee("bom-master") && (
              <SideLink collapsed={c} depth={1} active={isActive("/bom-master")}
                onClick={() => go("/bom-master")} icon={Stack} label="BOM Master" testId="nav-bom-master" />
            )}
            {canSee("process-master") && (
              <SideLink collapsed={c} depth={1} active={isActive("/process-master")}
                onClick={() => go("/process-master")} icon={Factory} label="Process Master" testId="nav-process-master" />
            )}
            {canSee("customer-master") && (
              <SideLink collapsed={c} depth={1} active={isActive("/customer-master")}
                onClick={() => go("/customer-master")} icon={UsersThree} label="Customer Master" testId="nav-customer-master" />
            )}
            {canSee("company-master") && (
              <SideLink collapsed={c} depth={1} active={isActive("/company-master")}
                onClick={() => go("/company-master")} icon={Buildings} label="Company Master" testId="nav-company-master" />
            )}
            {canSee("machine-master") && (
              <SideLink collapsed={c} depth={1} active={isActive("/machine-master")}
                onClick={() => go("/machine-master")} icon={Wrench} label="Machine Master" testId="nav-machine-master" />
            )}
            {canSee("suppliers") && (
              <SideLink collapsed={c} depth={1} active={isActive("/suppliers")}
                onClick={() => go("/suppliers")} icon={Users} label="Supplier Master" testId="nav-supplier-master" />
            )}
            {canSee("bom-routing") && (
              <SideLink collapsed={c} depth={1} active={isActive("/bom-routing")}
                onClick={() => go("/bom-routing")} icon={ListChecks} label="BOM Routing" testId="nav-bom-routing" />
            )}
            {canSee("bought-out") && (
              <SideLink collapsed={c} depth={1} active={isActive("/bought-out")}
                onClick={() => go("/bought-out")} icon={Package} label="Bought Out" testId="nav-bought-out" />
            )}
            {canSee("defect-master") && (
              <SideLink collapsed={c} depth={1} active={isActive("/defect-master")}
                onClick={() => go("/defect-master")} icon={WarningOctagon} label="Defect Master" testId="nav-defect-master" />
            )}
          </SideGroup>
        </>
      )}

      <div className="flex-1" />

      <SectionLabel collapsed={c}>Account</SectionLabel>

      {(user?.role === "Creator" || user?.role === "Admin") && (
        <SideLink collapsed={c} active={isActive("/customization")}
          onClick={() => go("/customization")} icon={Gear} label="Customization" testId="nav-customization" />
      )}
      {user?.role === "Creator" && (
        <SideLink collapsed={c} active={isActive("/settings")}
          onClick={() => go("/settings")} icon={GearSix} label="Settings" testId="nav-settings" />
      )}
      <SideLink collapsed={c} onClick={() => { logout(); navigate("/login"); }}
        icon={SignOut} label="Logout" testId="nav-logout" />
    </nav>
  );
}

// ─── Layout ────────────────────────────────────────────────────────
export default function Layout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const processes = useProcessesGlobal();
  const { companyName, hasJobWork } = useTopMeta();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_STATE_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_STATE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  const anyActive = (paths) => paths.some(p => location.pathname === p);

  const rmActive = anyActive(["/raw-material", "/rm-planning", "/rm-opening-stock"]);
  const processActive = location.pathname.startsWith("/operations") || location.pathname === "/job-work";
  const reportsActive = anyActive(["/final-report", "/work-order", "/daily-review", "/inventory", "/suppliers", "/stock-at-supplier"]);
  const costActive = anyActive(["/cost-sheet", "/budget-schedule", "/performance-dispatch"]) || location.pathname.startsWith("/cost-sheet/");
  const mastersActive = anyActive(["/part-master", "/bom-master", "/process-master", "/customer-master", "/company-master", "/machine-master", "/suppliers", "/bom-routing", "/bought-out", "/defect-master", "/items"]);

  // Group open/close state — auto-open the group whose section is active
  const [openGroups, setOpenGroups] = useState({
    rm: rmActive, proc: processActive, rep: reportsActive, cost: costActive, mst: mastersActive,
  });
  useEffect(() => {
    setOpenGroups(g => ({
      rm: g.rm || rmActive,
      proc: g.proc || processActive,
      rep: g.rep || reportsActive,
      cost: g.cost || costActive,
      mst: g.mst || mastersActive,
    }));
  }, [location.pathname, rmActive, processActive, reportsActive, costActive, mastersActive]);

  const toggleGroup = (key) => setOpenGroups(g => ({ ...g, [key]: !g[key] }));

  // Location split — a process may be In-house, Outsource, or both.
  // Fields default to true/false when the row was saved before Feb 27 2026.
  const isInhouse   = (p) => p.is_inhouse   === undefined ? true  : !!p.is_inhouse;
  const isOutsource = (p) => !!p.is_outsource;

  const machining        = processes.filter(p => p.code === "machining_p1" || p.code === "machining_p2");
  const nonMach          = processes.filter(p => p.code !== "machining_p1" && p.code !== "machining_p2");
  const inhouseProcs     = nonMach.filter(isInhouse);
  const outsourceProcs   = nonMach.filter(isOutsource);
  const anyOutsource     = outsourceProcs.length > 0;
  const otherProcs       = nonMach; // kept for legacy prop shape

  const go = (path) => { navigate(path); setMobileOpen(false); };

  const sidebarWidth = collapsed ? "w-16" : "w-60";

  const sidebarProps = {
    user, hasJobWork, otherProcs, machining,
    inhouseProcs, outsourceProcs, anyOutsource,
    isActive, rmActive, processActive, reportsActive, costActive, mastersActive,
    openGroups, toggleGroup, go, logout, navigate,
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* ═══════════════ SLIM BRAND HEADER ═══════════════ */}
      <header className="sticky top-0 z-30 bg-gradient-to-r from-[#131F42] via-[#1D2951] to-[#243766] border-b-[3px] border-[#D4A843] shadow-lg">
        <div className="flex items-center gap-3 px-3 md:px-4 h-14 md:h-[60px]">
          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-white hover:bg-white/10"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-nav-toggle"
            aria-label="Open navigation"
          >
            <List size={22} />
          </button>

          {/* Desktop sidebar toggle */}
          <button
            className="hidden md:inline-flex p-2 rounded-lg text-slate-200 hover:bg-white/10 hover:text-white"
            onClick={() => setCollapsed(v => !v)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="sidebar-toggle"
          >
            <SidebarSimple size={20} weight={collapsed ? "regular" : "fill"} />
          </button>

          {/* Brand */}
          <button onClick={() => navigate("/")} className="flex items-center gap-3 group focus:outline-none" data-testid="brand-home">
            <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-white/95 flex items-center justify-center overflow-hidden shadow-[0_4px_14px_-2px_rgba(212,168,67,0.55)] group-hover:shadow-[0_6px_18px_-2px_rgba(212,168,67,0.8)] transition-shadow">
              <img src={LOGO_URL} alt="Swami" className="h-8 w-8 md:h-9 md:w-9 object-contain" />
            </div>
            <div className="hidden sm:block leading-tight text-left">
              <div className="text-white font-black text-[14px] md:text-[15px] tracking-[0.14em]" style={{ fontFamily: 'Chivo' }}>
                SWAMI <span className="text-[#D4A843]">SOFTWARE</span>
              </div>
              <div className="text-[9px] md:text-[10px] uppercase tracking-[0.28em] text-slate-300 font-semibold">Production Tracker</div>
            </div>
          </button>

          {/* Optional company + page title */}
          {(companyName || title) && (
            <div className="hidden lg:flex items-center gap-2 ml-3 pl-3 border-l border-white/15 min-w-0">
              {companyName && (
                <span className="text-[10px] uppercase tracking-widest text-[#D4A843]/80 font-bold truncate">{companyName}</span>
              )}
              {companyName && title && <span className="text-white/30">·</span>}
              {title && <h1 className="text-sm font-black text-white truncate" data-testid="page-title">{title}</h1>}
            </div>
          )}

          {/* Right cluster */}
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden sm:block"><PeriodSelector /></div>

            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className="h-9 px-2.5 flex items-center gap-2 rounded-lg text-sm font-semibold text-slate-100 hover:bg-white/10 hover:text-white transition-all"
                data-testid="nav-user"
              >
                <span className="w-6 h-6 rounded-full bg-[#D4A843] text-[#1D2951] flex items-center justify-center font-black text-[11px]">
                  {(user?.username || "?").slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden md:inline max-w-[100px] truncate">{user?.username}</span>
                <CaretDown size={12} weight="bold" className="opacity-70" />
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1.5 min-w-[220px] bg-white rounded-lg shadow-2xl border border-[#E2E8F0] z-50 py-1.5">
                    <div className="px-3.5 py-2 border-b border-[#E2E8F0]">
                      <p className="text-[10px] text-[#94A3B8] uppercase tracking-widest">Signed in as</p>
                      <p className="font-bold text-sm text-[#1D2951]">{user?.username}</p>
                      <p className="text-[11px] text-[#D4A843] font-semibold">{user?.role}</p>
                    </div>
                    <button
                      onClick={() => { setUserMenuOpen(false); logout(); navigate("/login"); }}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-[#334155] hover:bg-[#F8FAFC] hover:text-[#1D2951]"
                      data-testid="nav-logout-top"
                    >
                      <SignOut size={15} className="text-[#94A3B8]" />
                      <span>Logout</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ═══════════════ SIDEBAR + BODY ═══════════════ */}
      <div className="flex" data-testid="app-shell">
        {/* Desktop sidebar */}
        <aside
          className={`hidden md:flex flex-col ${sidebarWidth} shrink-0 bg-gradient-to-b from-[#131F42] via-[#1D2951] to-[#131F42] border-r border-[#D4A843]/20 sticky top-[60px] transition-all duration-200 ease-out`}
          style={{ height: "calc(100vh - 60px)" }}
          data-testid="sidebar"
        >
          <SidebarBody isCollapsed={collapsed} {...sidebarProps} />
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <>
            <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} />
            <aside
              className="md:hidden fixed top-0 left-0 z-50 h-full w-72 bg-gradient-to-b from-[#131F42] via-[#1D2951] to-[#131F42] shadow-2xl flex flex-col"
              data-testid="sidebar-mobile"
            >
              <div className="h-14 flex items-center justify-between px-3 border-b border-white/10">
                <span className="text-white font-black tracking-widest text-sm">MENU</span>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-lg text-slate-200 hover:bg-white/10"
                  data-testid="mobile-nav-close"
                >
                  <X size={20} />
                </button>
              </div>
              <SidebarBody isCollapsed={false} forceExpanded {...sidebarProps} />
            </aside>
          </>
        )}

        {/* Page body */}
        <main className="flex-1 min-w-0" data-testid="page-body">
          {children}
        </main>
      </div>
    </div>
  );
}
